<script setup lang="ts">
    import type { FileItem } from '@/types';
    import { Download, Eye, File, Trash2 } from 'lucide-vue-next';
    import { useI18n } from 'vue-i18n';
    import { Button } from '@/components/ui/button';

    const { t } = useI18n();

    const props = withDefaults(
        defineProps<{
            file: FileItem;
            downloadable?: boolean;
            deletable?: boolean;
            previewable?: boolean;
        }>(),
        {
            downloadable: true,
            deletable: true,
            previewable: true,
        },
    );

    const emit = defineEmits<{
        (e: 'preview', file: FileItem): void;
        (e: 'download', file: FileItem): void;
        (e: 'delete', file: FileItem): void;
    }>();

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const preview = () => {
        emit('preview', props.file);
    };

    const download = () => {
        window.open(props.file.url, '_blank');
        emit('download', props.file);
    };

    const removeFile = () => {
        emit('delete', props.file);
    };
</script>

<template>
    <div
        class="flex w-full cursor-default items-center gap-2 rounded-md border bg-white p-2 text-xs transition-all"
    >
        <div class="flex w-8 items-center justify-center">
            <File class="text-muted-foreground size-4" />
        </div>

        <div class="flex min-w-0 flex-1 flex-col gap-1">
            <div class="text-foreground w-full truncate font-medium">{{ file.name }}</div>
            <div
                v-if="file.size"
                class="text-muted-foreground text-[10px]"
            >
                {{ formatFileSize(file.size) }}
            </div>
        </div>

        <div class="flex items-center">
            <Button
                v-if="previewable && file.url"
                variant="ghost_primary"
                size="icon"
                @click.prevent="preview"
            >
                <Eye class="size-4" />
            </Button>

            <Button
                v-if="downloadable && file.url"
                variant="ghost_primary"
                size="icon"
                @click.prevent="download"
            >
                <Download class="size-4" />
            </Button>

            <Button
                v-if="deletable"
                variant="ghost_destructive"
                size="icon"
                @click.prevent="removeFile"
            >
                <Trash2 class="size-4" />
            </Button>
        </div>
    </div>
</template>
